import { FC } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';

export interface IconButtonBadge {
  /** Icon shown inside the small badge pinned to the top-right corner. */
  icon: IconProp;
  /** Tailwind colour class for the badge glyph. Defaults to `text-mf-orange`. */
  colorClassName?: string;
}

export interface IconButtonProps {
  /** Icon rendered in the centre of the button. */
  icon: IconProp;
  onClick?: () => void;
  className?: string;
  ariaLabel?: string;
  /**
   * Optional badge pinned to the top-right corner — e.g. a Pro crown when a
   * freemium limit is reached. Omit for a plain icon button.
   */
  badge?: IconButtonBadge;
  disabled?: boolean;
  /** Shown on hover. */
  title?: string;
}

/** A small, round icon button (with an optional corner badge). */
const IconButton: FC<IconButtonProps> = ({
  icon,
  onClick,
  className,
  ariaLabel,
  badge,
  disabled,
  title,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={ariaLabel}
    disabled={disabled}
    title={title}
    className={classNames(styles.button, className)}
  >
    <FontAwesomeIcon icon={icon} className={styles.icon} />
    {badge && (
      <span className={styles.badge} aria-hidden="true">
        <FontAwesomeIcon
          icon={badge.icon}
          className={badge.colorClassName ?? 'text-mf-orange'}
        />
      </span>
    )}
  </button>
);

export default IconButton;

const styles = {
  button: classNames(
    'relative flex h-6 w-6 items-center justify-center rounded-full bg-ink-secondary/12 text-ink-primary',
    'cursor-pointer transition duration-150 ease-in-out',
    'hover:bg-accent-fill hover:text-white active:bg-accent-fill/85 active:text-white',
    'disabled:pointer-events-none disabled:opacity-40',
  ),
  icon: 'fa-fw text-sm',
  badge: classNames(
    'absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center',
    'rounded-full bg-mf-gray-600 text-[8px]',
  ),
};
